import {isNumber} from 'lodash';
import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {hoverSelector} from '../../../../libs/shared/helpers/src/browser/hoverHelpers';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {makeSizeConstants} from '../../../../libs/shared/ui/src/styles/styleHelpers';
import {boxShadow} from '../../../../libs/shared/ui/src/theme/themeStyleHelpers';

/*
 * Props.
 */

export interface AvatarProps {
  /** The size at which to render the avatar. */
  size: VisualSizesEnum | number;
  /** Optional color for the avatar border. */
  borderColor?: string;
  /** Optional hover color for the avatar border. */
  borderHoverColor?: string;
  className?: string;
}

/*
 * Style.
 */

export const avatarSizes = makeSizeConstants(16, 20, 30, 48);

export interface AvatarStyleProps {
  $size: VisualSizesEnum | number;
  $borderColor?: string;
  $borderHoverColor?: string;
}

const StyledAvatarDiv = styled.div<AvatarStyleProps>`
  flex-shrink: 0;
  position: relative;

  width: ${(p) => (isNumber(p.$size) ? p.$size : avatarSizes[p.$size])}px;
  height: ${(p) => (isNumber(p.$size) ? p.$size : avatarSizes[p.$size])}px;
  border-radius: 50%;

  ${(p) => maybeAddBorderStyle(p)};
  ${(p) => maybeAddBorderHoverStyle(p)};
`;

function maybeAddBorderStyle(props: AvatarStyleProps) {
  if (!props.$borderColor) {
    return '';
  }

  return css`
    ${(p) => boxShadow(p.theme, `0 0 0 2px ${props.$borderColor}`)};
  `;
}

function maybeAddBorderHoverStyle(props: AvatarStyleProps) {
  if (!props.$borderHoverColor) {
    return '';
  }

  return css`
    ${hoverSelector} {
      ${(p) => boxShadow(p.theme, `0 0 0 2px ${props.$borderHoverColor}`)};
    }
  `;
}

/*
 * Component.
 */

export const Avatar: FC<AvatarProps> = (props) => (
  <StyledAvatarDiv
    className={props.className}
    $size={props.size}
    $borderColor={props.borderColor}
    $borderHoverColor={props.borderHoverColor}
  >
    {props.children}
  </StyledAvatarDiv>
);
